"use client"

import { useEffect, useState } from "react"
import { Bell, Search, Plus, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Badge } from "@/components/ui/badge"
import { getAdminNotificationCounts, getAdminNotifications } from "@/lib/marketing-api"
import { useRouter } from "next/navigation"

interface Notification {
  _id: string
  type: string
  message: string
  read: boolean
  createdAt: string
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return "Just now"
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function useNotifications(): { notifications: Notification[]; unread: number; loading: boolean } {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unread, setUnread] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetch = async () => {
      try {
        const [listRes, countRes] = await Promise.all([
          getAdminNotifications({ page: 1, limit: 5 }),
          getAdminNotificationCounts(),
        ])
        setNotifications((listRes as any)?.data || [])
        setUnread((countRes as any)?.data?.unread || 0)
      } catch {
        setNotifications([])
        setUnread(0)
      } finally {
        setLoading(false)
      }
    }

    fetch()
    const interval = setInterval(fetch, 30000)
    return () => clearInterval(interval)
  }, [])

  return { notifications, unread, loading }
}

interface DashboardHeaderProps {
  title: string
  description?: string
  showCreateButton?: boolean
  onCreateClick?: () => void
  showSearch?: boolean
  searchPlaceholder?: string
  createButtonLabel?: string
}

export function DashboardHeader({
  title,
  description,
  showCreateButton = true,
  onCreateClick,
  showSearch = true,
  searchPlaceholder = "Search campaigns, creatives...",
  createButtonLabel = "New Campaign",
}: DashboardHeaderProps) {
  const { notifications, unread, loading: notifsLoading } = useNotifications()
  const router = useRouter()

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 md:px-6">
      <div className="flex items-center gap-4">
        <SidebarTrigger />
        <div>
          <h1 className="text-lg font-semibold text-foreground">{title}</h1>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative hidden md:block">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search campaigns, creatives..."
            className="w-64 pl-9 bg-secondary border-border"
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5 text-muted-foreground" />
              {unread > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-[10px]">
                  {unread > 99 ? "99+" : unread}
                </Badge>
              )}
              <span className="sr-only">Notifications</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel>Notifications</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {notifsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">No notifications</div>
            ) : (
              <>
                {notifications.map((n) => (
                  <DropdownMenuItem
                    key={n._id}
                    className="flex flex-col items-start gap-1 py-3 cursor-pointer"
                    onClick={() => router.push('/notifications')}
                  >
                    <span className={n.read ? "text-sm text-muted-foreground" : "font-medium"}>{n.message}</span>
                    <span className="text-xs text-muted-foreground">{timeAgo(n.createdAt)}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem className="justify-center font-medium cursor-pointer" onClick={() => router.push('/notifications')}>
                  View all notifications
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {showCreateButton && (
          <Button size="sm" className="hidden gap-2 lg:flex" onClick={onCreateClick}>
            <Plus className="h-4 w-4" />
            {createButtonLabel}
          </Button>
        )}
      </div>
    </header>
  )
}
